import React from 'react';
import styled from 'styled-components';

/**
 * Chip Component - Multi-select button for preferences
 *
 * @param {Object} props
 * @param {string} props.label - Text to display in the chip
 * @param {boolean} props.selected - Whether the chip is selected
 * @param {Function} props.onSelect - Callback when chip is clicked
 * @param {'standard'|'input'} [props.variant='standard'] - Visual variant
 * @param {Function} [props.onRemove] - Callback when close icon clicked (input variant only)
 * @param {string} [props.className] - Additional CSS class
 * @param {boolean} [props.disabled] - Whether the chip is disabled
 * @param {string|React.ReactNode} [props.icon] - Material icon name (string) or custom icon element (React node) to display before label
 */
const Chip = ({
  label,
  selected = false,
  onSelect,
  variant = 'standard',
  onRemove,
  className,
  disabled = false,
  icon,
}) => {
  const handleClick = (e) => {
    e.stopPropagation();
    if (!disabled && onSelect) {
      onSelect();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (!disabled && onRemove) {
      onRemove();
    }
  };

  const handleKeyDown = (e) => {
    // Handle keyboard navigation
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <ChipButton
      type="button"
      role="button"
      aria-pressed={selected}
      aria-label={label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      $selected={selected}
      $variant={variant}
      $disabled={disabled}
      $hasIcon={!!icon}
      tabIndex={disabled ? -1 : 0}
    >
      {icon && (
        typeof icon === 'string' ? (
          <ChipIcon className="material-icons">
            {icon}
          </ChipIcon>
        ) : (
          <ChipIcon as="span">
            {icon}
          </ChipIcon>
        )
      )}

      <ChipLabel $selected={selected} $variant={variant}>
        {label}
      </ChipLabel>

      {variant === 'input' && (
        <CloseIcon
          onClick={handleRemove}
          aria-label={`Remove ${label}`}
          $disabled={disabled}
        >
          ×
        </CloseIcon>
      )}
    </ChipButton>
  );
};

const ChipButton = styled.button`
  /* Layout */
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${props => props.$hasIcon ? '4px 8px 4px 8px' : '4px 8px'};
  min-height: 36px;

  /* Border and background */
  background: ${props => props.$selected ? 'rgba(69, 68, 107, 0.1)' : 'transparent'};
  border: 1px solid rgba(69, 68, 107, 0.3);
  border-radius: 50px;

  /* Cursor */
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};

  /* Transitions */
  transition:
    background-color 150ms ease-in-out,
    border-color 150ms ease-in-out;
  will-change: transform;

  /* Remove default button styles */
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  /* Hover state (web only) */
  &:hover:not(:disabled) {
    background: ${props => props.$selected ? 'rgba(69, 68, 107, 0.2)' : 'rgba(69, 68, 107, 0.1)'};
    transition-duration: 100ms;
  }

  /* Focus state for accessibility */
  &:focus-visible {
    outline: 2px solid #45446b;
    outline-offset: 2px;
  }

  /* Active state */
  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  /* Input variant specific styles */
  ${props => props.$variant === 'input' && `
    padding: 6px 8px 6px 12px;
  `}
`;

const ChipIcon = styled.span`
  /* Layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* Typography */
  font-size: 16px;
  line-height: 1;
  color: #45446b;

  /* Prevent icon selection */
  user-select: none;
`;

const ChipLabel = styled.span`
  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: ${props => props.$selected ? 600 : 500};
  font-size: ${props => props.$variant === 'input' ? '12px' : '14px'};
  line-height: 1.4;
  letter-spacing: 0.5px;
  color: #45446b;

  /* Prevent text selection */
  user-select: none;

  /* Ensure text doesn't wrap */
  white-space: nowrap;
`;

const CloseIcon = styled.span`
  /* Layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 4px;

  /* Typography */
  font-size: 18px;
  line-height: 1;
  color: #45446b;

  /* Cursor */
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};

  /* Hover effect */
  transition: transform 100ms ease-in-out;

  &:hover:not(:disabled) {
    transform: scale(1.2);
  }
`;

export default Chip;
